import { useEffect, useReducer } from 'react'
import { useActivate } from 'react-activation'
import { isResponseCodeSuccess } from '@/tools/util'
import { message } from 'antd'
import reducer, {
  initialState,
  setCheckedRadioKey,
  setPagination,
  setSearchParam,
  setDataSource,
  setIsTableLoading,
  setSelectedRows,
  setDrawerVisible,
  setProcessDateSource,
  setIsProcessTableLoading,
  setProcessPagination,
  setCurrentProcess,
} from './reducer'
import { history, useTypedSelector } from '@/redux/store'
import { userRouter } from '@/route'
import {
  GET_REPORT_MATERIAL_LIST,
  GET_REPORT_MATERIAL_LOG,
  EXPORT_REPORT_MATERIAL_LIST,
} from '@/api/reportApi'
import { getSupplierId } from '@/tools/util'

export default function useHook() {
  const [state, dispatch] = useReducer(reducer, initialState)
  const {
    checkedRadioKey,
    currentRadioDict,
    dataSource: dateSource,
    isTableLoading,
    pagination,
    searchParam,
    selectedRows,
    processPagination,
    currentProcess,
  } = state
  const supRoute = userRouter[1].url

  const { platformList } = useTypedSelector((state) => state.goodsReducer)

  useEffect(() => {
    getData({
      ...searchParam,
      pageNo: 1,
      pageSize: pagination.pagesize,
    })
  }, [])

  // 从详情页返回时页面被 KeepAlive 保活，按当前页签/筛选/页码重新取数
  useActivate(() => {
    getData({
      ...searchParam,
      reportStatus: (currentRadioDict as any)?.[checkedRadioKey]?.key,
      pageNo: pagination.current,
      pageSize: pagination.pagesize,
    })
  })

  // 日期转换函数
  const convertDateToTimestamp = (dateStr: string, isEndTime = false) => {
    if (!dateStr) return undefined
    const date = new Date(dateStr)
    if (isEndTime) {
      // 结束时间设置为 23:59:59
      date.setHours(23, 59, 59, 999)
    } else {
      // 开始时间设置为 00:00:00
      date.setHours(0, 0, 0, 0)
    }
    return date.getTime()
  }
  async function getData(param: any) {
    const { pageNo, pageSize } = param
    dispatch(setIsTableLoading(true))
    const reportStatusParams = param.reportStatus
      ? param.reportStatus
      : (currentRadioDict as any)?.[checkedRadioKey]?.key
    const params = {
      ...param,
      supplierId: getSupplierId(),
      reportStatus: reportStatusParams === '全部' ? '' : reportStatusParams,
      materialCode: param.materialCodes,
      // 转换日期参数为时间戳
      reportStartTime: convertDateToTimestamp(param.reportStartTime, false),
      reportEndTime: convertDateToTimestamp(param.reportEndTime, true),
      auditStartTime: convertDateToTimestamp(param.auditStartTime, false),
      auditEndTime: convertDateToTimestamp(param.auditEndTime, true),
    }
    const res = await GET_REPORT_MATERIAL_LIST(params)

    if (isResponseCodeSuccess(res.code)) {
      // 设置当前表格数据
      dispatch(setDataSource(res.data.content))
      // 设置分页
      dispatch(
        setPagination({
          ...pagination,
          pagesize: pageSize ? pageSize : pagination.pagesize,
          current: pageNo ? pageNo : pagination.current,
          total: res.data.totalCount,
        }),
      )
      dispatch(setIsTableLoading(false))
    } else {
      message.warning(res.msg)
    }
  }

  const handleCheckedChange = (key: any) => {
    dispatch(setCheckedRadioKey(key))
    getData({
      ...searchParam,
      reportStatus: (currentRadioDict as any)?.[key].key,
      pageNo: 1,
      pageSize: initialState.pagination.pagesize,
    })
  }

  const handleSearchClick = (value: any) => {
    getData({
      ...value,
      pageNo: 1,
      pageSize: pagination.pagesize,
    })
    dispatch(setSearchParam(value))
  }

  function handlePaginationChange(page: number, pageSize?: number) {
    getData({
      ...searchParam,
      pageNo: page,
      pageSize: pageSize ?? pagination.pagesize,
    })
  }

  const handleTableRefresh = async () => {
    getData({
      ...searchParam,
      reportStatus: (currentRadioDict as any)?.[checkedRadioKey]?.key,
      pageNo: 1,
      pageSize: pagination.pagesize,
    })
  }
  const handleTableChangeCheck = (selectedRowKeys: any) => {
    dispatch(setSelectedRows(selectedRowKeys))
  }
  const handleExportData = async () => {
    const params = {
      ids: selectedRows,
      ...searchParam,
      reportStatus:
        checkedRadioKey === '全部'
          ? ''
          : (currentRadioDict as any)?.[checkedRadioKey]?.key,
      // 转换日期参数为时间戳
      reportStartTime: convertDateToTimestamp(
        searchParam.reportStartTime,
        false,
      ),
      supplierId: getSupplierId(),
      reportEndTime: convertDateToTimestamp(searchParam.reportEndTime, true),
      auditStartTime: convertDateToTimestamp(searchParam.auditStartTime, false),
      auditEndTime: convertDateToTimestamp(searchParam.auditEndTime, true),
    }
    const res = await EXPORT_REPORT_MATERIAL_LIST(params)
    if (isResponseCodeSuccess(res.code)) {
      message.success(res.msg)
    } else {
      message.warning(res.msg)
    }
  }

  const handleProcessData = (record: any) => {
    dispatch(setCurrentProcess(record))
    dispatch(setDrawerVisible(true))
    getProcessData({
      id: record.id,
      pageNo: 1,
      pageSize: processPagination.pagesize,
    })
  }
  const getProcessData = async (param: any) => {
    dispatch(setIsProcessTableLoading(true))
    const { pageNo, pageSize } = param

    const res = await GET_REPORT_MATERIAL_LOG(param.id || currentProcess.id, {
      pageNo,
      pageSize,
    })
    if (isResponseCodeSuccess(res.code)) {
      dispatch(setProcessDateSource(res.data.content))
      dispatch(
        setProcessPagination({
          ...processPagination,
          pagesize: pageSize ? pageSize : processPagination.pagesize,
          current: pageNo ? pageNo : processPagination.current,
          total: res.data.totalCount,
        }),
      )
      dispatch(setIsProcessTableLoading(false))
    }
  }
  const handleProcessPaginationChange = (page: number, pageSize?: number) => {
    getProcessData({
      pageNo: page,
      pageSize: pageSize ?? processPagination.pagesize,
    })
  }
  const handleCloseDrawer = () => {
    dispatch(setDrawerVisible(false))
    dispatch(setCurrentProcess(''))
  }

  const handleUpdateData = (info: any) => {
    const showDiscountRate =
      platformList.find((item) => item.code === info.projectCode)
        ?.showDiscountRate ?? '0'

    // 详情
    history.replace({
      pathname: `${supRoute}/goodsSubmission`,
      state: {
        id: info.id,
        BtnState: info.reportStatus === 'REJECTED' ? ['Edit'] : ['Detail'],
        submissionType: info.reportType,
        platformCode: info.platformCode,
        showDiscountRate,
      },
    })
  }
  return {
    checkedRadioKey,
    currentRadioDict,
    dateSource,
    isTableLoading,
    pagination,
    handleCheckedChange,
    handlePaginationChange,
    handleSearchClick,
    handleTableRefresh,
    state,
    handleTableChangeCheck,
    handleExportData,
    handleProcessData,
    handleProcessPaginationChange,
    handleCloseDrawer,
    handleUpdateData,
  }
}
import { useEffect, useReducer } from 'react'
import { useActivate } from 'react-activation'
import { isResponseCodeSuccess } from '@/tools/util'
import { message } from 'antd'
import reducer, {
  initialState,
  setCheckedRadioKey,
  setPagination,
  setSearchParam,
  setDataSource,
  setIsTableLoading,
  setSelectedRows,
  setDrawerVisible,
  setProcessDateSource,
  setIsProcessTableLoading,
  setProcessPagination,
  setCurrentProcess,
} from './reducer'
import { history, useTypedSelector } from '@/redux/store'
import { userRouter } from '@/route'
import {
  GET_REPORT_MATERIAL_LIST,
  GET_REPORT_MATERIAL_LOG,
  EXPORT_REPORT_MATERIAL_LIST,
} from '@/api/reportApi'
import { getSupplierId } from '@/tools/util'

export default function useHook() {
  const [state, dispatch] = useReducer(reducer, initialState)
  const {
    checkedRadioKey,
    currentRadioDict,
    dataSource: dateSource,
    isTableLoading,
    pagination,
    searchParam,
    selectedRows,
    processPagination,
    currentProcess,
  } = state
  const supRoute = userRouter[1].url

  const { platformList } = useTypedSelector((state) => state.goodsReducer)

  useEffect(() => {
    getData({
      ...searchParam,
      pageNo: 1,
      pageSize: pagination.pagesize,
    })
  }, [])

  // 从详情页返回时页面被 KeepAlive 保活，按当前页签/筛选/页码重新取数
  useActivate(() => {
    getData({
      ...searchParam,
      reportStatus: (currentRadioDict as any)?.[checkedRadioKey]?.key,
      pageNo: pagination.current,
      pageSize: pagination.pagesize,
    })
  })

  // 日期转换函数
  const convertDateToTimestamp = (dateStr: string, isEndTime = false) => {
    if (!dateStr) return undefined
    const date = new Date(dateStr)
    if (isEndTime) {
      // 结束时间设置为 23:59:59
      date.setHours(23, 59, 59, 999)
    } else {
      // 开始时间设置为 00:00:00
      date.setHours(0, 0, 0, 0)
    }
    return date.getTime()
  }
  async function getData(param: any) {
    const { pageNo, pageSize } = param
    dispatch(setIsTableLoading(true))
    const reportStatusParams = param.reportStatus
      ? param.reportStatus
      : (currentRadioDict as any)?.[checkedRadioKey]?.key
    const params = {
      ...param,
      supplierId: getSupplierId(),
      reportStatus: reportStatusParams === '全部' ? '' : reportStatusParams,
      materialCode: param.materialCodes,
      // 转换日期参数为时间戳
      reportStartTime: convertDateToTimestamp(param.reportStartTime, false),
      reportEndTime: convertDateToTimestamp(param.reportEndTime, true),
      auditStartTime: convertDateToTimestamp(param.auditStartTime, false),
      auditEndTime: convertDateToTimestamp(param.auditEndTime, true),
    }
    const res = await GET_REPORT_MATERIAL_LIST(params)

    if (isResponseCodeSuccess(res.code)) {
      // 设置当前表格数据
      dispatch(setDataSource(res.data.content))
      // 设置分页
      dispatch(
        setPagination({
          ...pagination,
          pagesize: pageSize ? pageSize : pagination.pagesize,
          current: pageNo ? pageNo : pagination.current,
          total: res.data.totalCount,
        }),
      )
      dispatch(setIsTableLoading(false))
    } else {
      message.warning(res.msg)
    }
  }

  const handleCheckedChange = (key: any) => {
    dispatch(setCheckedRadioKey(key))
    getData({
      ...searchParam,
      reportStatus: (currentRadioDict as any)?.[key].key,
      pageNo: 1,
      pageSize: initialState.pagination.pagesize,
    })
  }

  const handleSearchClick = (value: any) => {
    getData({
      ...value,
      pageNo: 1,
      pageSize: pagination.pagesize,
    })
    dispatch(setSearchParam(value))
  }

  function handlePaginationChange(page: number, pageSize?: number) {
    getData({
      ...searchParam,
      pageNo: page,
      pageSize: pageSize ?? pagination.pagesize,
    })
  }

  const handleTableRefresh = async () => {
    getData({
      ...searchParam,
      reportStatus: (currentRadioDict as any)?.[checkedRadioKey]?.key,
      pageNo: 1,
      pageSize: pagination.pagesize,
    })
  }
  const handleTableChangeCheck = (selectedRowKeys: any) => {
    dispatch(setSelectedRows(selectedRowKeys))
  }
  const handleExportData = async () => {
    const params = {
      ids: selectedRows,
      ...searchParam,
      reportStatus:
        checkedRadioKey === '全部'
          ? ''
          : (currentRadioDict as any)?.[checkedRadioKey]?.key,
      // 转换日期参数为时间戳
      reportStartTime: convertDateToTimestamp(
        searchParam.reportStartTime,
        false,
      ),
      supplierId: getSupplierId(),
      reportEndTime: convertDateToTimestamp(searchParam.reportEndTime, true),
      auditStartTime: convertDateToTimestamp(searchParam.auditStartTime, false),
      auditEndTime: convertDateToTimestamp(searchParam.auditEndTime, true),
    }
    const res = await EXPORT_REPORT_MATERIAL_LIST(params)
    if (isResponseCodeSuccess(res.code)) {
      message.success(res.msg)
    } else {
      message.warning(res.msg)
    }
  }

  const handleProcessData = (record: any) => {
    dispatch(setCurrentProcess(record))
    dispatch(setDrawerVisible(true))
    getProcessData({
      id: record.id,
      pageNo: 1,
      pageSize: processPagination.pagesize,
    })
  }
  const getProcessData = async (param: any) => {
    dispatch(setIsProcessTableLoading(true))
    const { pageNo, pageSize } = param

    const res = await GET_REPORT_MATERIAL_LOG(param.id || currentProcess.id, {
      pageNo,
      pageSize,
    })
    if (isResponseCodeSuccess(res.code)) {
      dispatch(setProcessDateSource(res.data.content))
      dispatch(
        setProcessPagination({
          ...processPagination,
          pagesize: pageSize ? pageSize : processPagination.pagesize,
          current: pageNo ? pageNo : processPagination.current,
          total: res.data.totalCount,
        }),
      )
      dispatch(setIsProcessTableLoading(false))
    }
  }
  const handleProcessPaginationChange = (page: number, pageSize?: number) => {
    getProcessData({
      pageNo: page,
      pageSize: pageSize ?? processPagination.pagesize,
    })
  }
  const handleCloseDrawer = () => {
    dispatch(setDrawerVisible(false))
    dispatch(setCurrentProcess(''))
  }

  const handleUpdateData = (info: any) => {
    const showDiscountRate =
      platformList.find((item) => item.code === info.projectCode)
        ?.showDiscountRate ?? '0'

    // 详情
    history.replace({
      pathname: `${supRoute}/goodsSubmission`,
      state: {
        id: info.id,
        BtnState: info.reportStatus === 'REJECTED' ? ['Edit'] : ['Detail'],
        submissionType: info.reportType,
        platformCode: info.platformCode,
        showDiscountRate,
      },
    })
  }
  return {
    checkedRadioKey,
    currentRadioDict,
    dateSource,
    isTableLoading,
    pagination,
    handleCheckedChange,
    handlePaginationChange,
    handleSearchClick,
    handleTableRefresh,
    state,
    handleTableChangeCheck,
    handleExportData,
    handleProcessData,
    handleProcessPaginationChange,
    handleCloseDrawer,
    handleUpdateData,
  }
}
